import { useEffect, useState } from "react";
import { useQuizStore } from "../state/store";
import { CounterHeader } from "./CounterHeader";
import { ProgressBar } from "./ProgressBar";
import { LetterBadge } from "./LetterBadge";
import { ChoiceButton, type ChoiceVariant } from "./ChoiceButton";
import type { Choice } from "../lib/distractors";
import type { Feedback, HistoryEntry } from "../state/store";
import type { Direction, LearningMode, VocabEntry } from "../lib/types";

export function QuizScreen() {
  const counters = useQuizStore((s) => s.counters);
  const currentPrompt = useQuizStore((s) => s.currentPrompt);
  const currentDirection = useQuizStore((s) => s.currentDirection);
  const currentChoices = useQuizStore((s) => s.currentChoices);
  const currentFeedback = useQuizStore((s) => s.feedback);
  const history = useQuizStore((s) => s.history);
  const historyIndex = useQuizStore((s) => s.historyIndex);
  const recordAnswer = useQuizStore((s) => s.recordAnswer);
  const nextPrompt = useQuizStore((s) => s.nextPrompt);
  const goBack = useQuizStore((s) => s.goBack);
  const goForward = useQuizStore((s) => s.goForward);
  const markers = useQuizStore((s) => s.markers);
  const toggleMarked = useQuizStore((s) => s.toggleMarked);
  const learningMode = useQuizStore((s) => s.settings.learningMode);

  const viewingHistory = historyIndex !== null;
  const historyEntry: HistoryEntry | null = viewingHistory ? (history[historyIndex] ?? null) : null;

  const prompt: VocabEntry | null = viewingHistory ? (historyEntry?.prompt ?? null) : currentPrompt;
  const direction: Direction = viewingHistory
    ? (historyEntry?.direction ?? "en_de")
    : (currentDirection ?? "en_de");
  const choices: Choice[] = viewingHistory ? (historyEntry?.choices ?? []) : currentChoices;
  const feedback: Feedback = viewingHistory ? (historyEntry?.feedback ?? { kind: "none" }) : currentFeedback;

  const isAnswered = feedback.kind !== "none";
  const canGoBack = historyIndex === null ? history.length > 0 : historyIndex < history.length - 1;
  const canGoForward = viewingHistory;

  const [showExamples, setShowExamples] = useState(false);
  // Reset the examples toggle whenever we switch prompts.
  useEffect(() => {
    setShowExamples(false);
  }, [prompt?.english, historyIndex]);

  function advance() {
    if (viewingHistory) {
      goForward();
    } else {
      void nextPrompt();
    }
  }

  useEffect(() => {
    function onKey(ev: KeyboardEvent) {
      if (ev.key >= "1" && ev.key <= "4") {
        const idx = Number(ev.key) - 1;
        if (!viewingHistory && !isAnswered && idx < choices.length) {
          const choice = choices[idx];
          if (choice) {
            ev.preventDefault();
            void recordAnswer(choice.german);
          }
        }
      } else if (ev.key === "Enter" || ev.key === " ") {
        if (isAnswered) {
          ev.preventDefault();
          advance();
        }
      } else if (ev.key === "ArrowLeft" && canGoBack) {
        ev.preventDefault();
        goBack();
      } else if (ev.key === "ArrowRight" && canGoForward) {
        ev.preventDefault();
        goForward();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [choices, isAnswered, viewingHistory, canGoBack, canGoForward]);

  if (!prompt) {
    return <p className="text-fg-soft">Lade nächste Frage …</p>;
  }

  const chosenGerman = viewingHistory ? historyEntry?.chosenGerman : undefined;

  const variantFor = (germanThisButton: string): ChoiceVariant => {
    if (feedback.kind === "none") return "idle";
    if (feedback.kind === "correct") {
      return germanThisButton === feedback.correctGerman ? "correct" : "dimmed";
    }
    // wrong
    if (germanThisButton === feedback.correctGerman) return "revealed-correct";
    if (germanThisButton === feedback.chosenGerman) return "wrong";
    if (germanThisButton === chosenGerman) return "wrong";
    return "dimmed";
  };

  return (
    <div className="space-y-5 pb-24 sm:pb-0">
      <CounterHeader counters={counters} />
      <ProgressBar counters={counters} />

      {viewingHistory && (
        <div
          className="bg-bg/60 border border-outline-soft rounded-xl px-3 py-2 text-xs text-fg-muted flex items-center justify-between gap-2"
          data-testid="history-banner"
        >
          <span>
            Rückblick {historyIndex! + 1} / {history.length} — diese Antwort ist schon gespeichert.
          </span>
        </div>
      )}

      <div
        className="relative bg-card rounded-2xl border border-outline shadow-card p-4 sm:p-6 space-y-5"
        data-testid="quiz-card"
        data-direction={direction}
        data-learning-mode={learningMode}
      >
        {/* Direction-Akzentstreifen links — blau für EN→DE, amber für DE→EN.
            Pre-kognitive Markierung, besonders wichtig im Gemischt-Modus. */}
        <span
          aria-hidden
          className={`absolute top-3 left-0 w-1 rounded-r-full ${
            direction === "en_de" ? "bg-accent" : "bg-warning"
          }`}
          style={{ height: "calc(100% - 24px)" }}
        />
        {/* Markiert-Modus-Streifen oben — warmgelb, sichtbar nur wenn der
            User aktuell im 'marked'-Modus lernt (Issue #123, Spec 50 §7). */}
        {learningMode === "marked" && (
          <span
            aria-hidden
            className="absolute top-0 left-3 right-3 h-1 rounded-b-full bg-warning"
          />
        )}
        <div className="flex items-center justify-between gap-2">
          <LetterBadge letter={prompt.letter} abbreviation={prompt.abbreviation} />
          <div className="flex items-center gap-1.5">
            {learningMode === "marked" && <ModeBadge mode={learningMode} />}
            <DirectionPill direction={direction} />
            <StarToggle
              english={prompt.english}
              marked={markers.has(prompt.english)}
              disabled={viewingHistory}
              onToggle={() => void toggleMarked(prompt.english)}
            />
          </div>
        </div>

        <h1
          className="text-3xl sm:text-5xl font-bold tracking-tight break-words leading-tight"
          data-testid="quiz-prompt"
          lang={direction === "en_de" ? "en" : "de"}
        >
          {direction === "en_de" ? prompt.english : prompt.german}
        </h1>

        <div className="grid grid-cols-1 gap-2 pt-1">
          {choices.map((c, i) => (
            <ChoiceButton
              key={c.german}
              index={i + 1}
              german={c.german}
              variant={variantFor(c.german)}
              disabled={isAnswered}
              onClick={() => {
                if (viewingHistory) return;
                void recordAnswer(c.german);
              }}
            />
          ))}
        </div>

        <FeedbackBanner feedback={feedback} />

        {isAnswered && prompt.examples && prompt.examples.length > 0 && (
          <ExamplesPanel
            examples={prompt.examples}
            open={showExamples}
            onToggle={() => setShowExamples((v) => !v)}
          />
        )}

        {/* Desktop / wide: inline action row */}
        <div className="hidden sm:flex items-center justify-between gap-2 pt-1">
          <p className="text-xs text-fg-faint">
            Tasten: 1–4 wählen · ← Zurück · → / Enter = Weiter
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={goBack}
              disabled={!canGoBack}
              className="min-h-[44px] px-3 py-2 rounded-lg bg-surface border border-outline text-fg-soft text-sm font-semibold hover:bg-card hover:text-fg disabled:opacity-30 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-outline-strong transition-colors"
              data-testid="back"
              aria-label="Zur letzten Frage"
            >
              ← Zurück
            </button>
            <button
              type="button"
              onClick={advance}
              disabled={!isAnswered}
              className="min-h-[48px] px-5 py-2.5 rounded-xl bg-accent text-accent-on text-base font-semibold shadow-card hover:bg-accent-hover active:bg-accent-active active:scale-[0.985] transition-all disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
              data-testid="advance"
            >
              {viewingHistory ? "Weiter →" : "Weiter ↵"}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile sticky action bar */}
      <div
        className="sm:hidden fixed bottom-0 left-0 right-0 z-30 bg-bg/95 backdrop-blur border-t border-outline px-3 pt-3 safe-bottom"
        aria-hidden={!isAnswered && !canGoBack}
      >
        <div className="flex items-stretch gap-2">
          <button
            type="button"
            onClick={goBack}
            disabled={!canGoBack}
            className="min-h-[52px] px-4 rounded-xl bg-surface border border-outline text-fg-soft text-sm font-semibold disabled:opacity-30 disabled:cursor-not-allowed hover:bg-card focus:outline-none focus-visible:ring-2 focus-visible:ring-outline-strong"
            data-testid="back-mobile"
            aria-label="Zur letzten Frage"
          >
            ←
          </button>
          <button
            type="button"
            onClick={advance}
            disabled={!isAnswered}
            className="flex-1 min-h-[52px] rounded-xl bg-accent text-accent-on text-base font-semibold shadow-card hover:bg-accent-hover active:bg-accent-active active:scale-[0.985] transition-all disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
            data-testid="advance-mobile"
          >
            {viewingHistory ? "Weiter →" : "Weiter ↵"}
          </button>
        </div>
      </div>
    </div>
  );
}

function DirectionPill({ direction }: { direction: Direction }) {
  const isEnDe = direction === "en_de";
  const label = isEnDe ? "EN → DE · Übersetzen" : "DE → EN · Übersetzen";
  const className = isEnDe
    ? "bg-accent-soft text-accent border-accent/35"
    : "bg-warning-soft text-warning border-warning/40";
  return (
    <span
      data-testid="direction-pill"
      data-direction={direction}
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-[10px] sm:text-[11px] font-bold tracking-[0.12em] uppercase ${className}`}
    >
      {label}
    </span>
  );
}

function ModeBadge({ mode }: { mode: LearningMode }) {
  if (mode !== "marked") return null;
  return (
    <span
      data-testid="mode-pill"
      data-mode={mode}
      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border bg-warning-soft text-warning border-warning/40 text-[10px] sm:text-[11px] font-bold tracking-[0.12em] uppercase"
    >
      ★ Markiert
    </span>
  );
}

/**
 * Stern-Toggle für den Quiz-Karten-Header (Issue #123). Touch-Target ≥44 px
 * für mobile. Im History-Modus disabled, weil die Karte read-only ist — der
 * User sieht eine schon beantwortete frage; Markierung auf das `english` ist
 * dort verwirrend, weil die Aktion sich nicht visuell zurückbeziehen würde.
 */
function StarToggle({
  english,
  marked,
  disabled,
  onToggle,
}: {
  english: string;
  marked: boolean;
  disabled: boolean;
  onToggle: () => void;
}) {
  const label = marked ? "Markierung entfernen" : "Vokabel markieren";
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={disabled}
      aria-pressed={marked}
      aria-label={label}
      title={label}
      data-testid="star-toggle"
      data-english={english}
      data-marked={marked}
      className={`min-w-[44px] min-h-[44px] inline-flex items-center justify-center rounded-full border text-xl transition-colors ${
        marked
          ? "bg-warning-soft text-warning border-warning/40"
          : "bg-bg/40 text-fg-faint border-outline-soft hover:text-warning hover:border-warning/30"
      } disabled:opacity-30 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-warning`}
    >
      <span aria-hidden>{marked ? "★" : "☆"}</span>
    </button>
  );
}

function FeedbackBanner({ feedback }: { feedback: Feedback }) {
  if (feedback.kind === "none") return null;
  if (feedback.kind === "correct") {
    return (
      <div
        className="bg-success-soft border border-success/40 rounded-xl px-4 py-3 text-success animate-slide-up"
        role="status"
        aria-live="polite"
      >
        <span className="font-semibold">Richtig</span> — {feedback.correctGerman}
      </div>
    );
  }
  return (
    <div
      className="bg-danger-soft border border-danger/40 rounded-xl px-4 py-3 text-danger animate-slide-up"
      role="status"
      aria-live="polite"
    >
      <span className="font-semibold">Falsch</span> — richtig wäre:{" "}
      <span className="font-semibold">{feedback.correctGerman}</span>
    </div>
  );
}

function ExamplesPanel({
  examples,
  open,
  onToggle,
}: {
  examples: string[];
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="space-y-2">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="w-full min-h-[44px] px-4 py-2.5 rounded-lg bg-surface border border-outline text-fg-soft text-sm font-semibold hover:bg-card hover:text-fg focus:outline-none focus-visible:ring-2 focus-visible:ring-outline-strong transition-colors flex items-center justify-between"
        data-testid="examples-toggle"
      >
        <span className="inline-flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-accent-soft text-accent text-xs font-bold">
            i
          </span>
          {open ? "Beispiele ausblenden" : "Beispiele zeigen"}
        </span>
        <span className="text-fg-faint" aria-hidden>
          {open ? "▴" : "▾"}
        </span>
      </button>
      {open && (
        <ul
          className="bg-bg/40 border border-outline-soft rounded-lg p-3 space-y-2 animate-fade-in"
          data-testid="examples-panel"
        >
          {examples.map((ex, i) => (
            <li
              key={i}
              className="text-sm text-fg-soft leading-relaxed pl-3 border-l-2 border-accent/50 italic"
            >
              {ex}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
